import React from "react";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import { ShieldExclamationIcon } from "@heroicons/react/20/solid";
import { Badge } from "src/components/base/badge";
import { Link } from "src/components/base/link";
import { SecurityFinding, Severity } from "./security-analysis";

/**
 * The properties for {@link InsecureSettingCard}
 */
export type InsecureSettingCardProps = {
    /** The finding to render */
    finding: SecurityFinding;
};

/**
 * CSS classes for a severity level
 */
type SeverityStyle = {
    /** Icon background */
    iconBg: string;
    /** Icon text color */
    iconColor: string;
    /** Badge color */
    badge: "red" | "orange" | "amber";
    /** Count text color */
    count: string;
};

const SEVERITY_STYLES: Record<Severity, SeverityStyle> = {
    critical: {
        iconBg: "bg-red-500/10 dark:bg-red-500/20",
        iconColor: "text-red-500",
        badge: "red",
        count: "text-red-600 dark:text-red-400",
    },
    high: {
        iconBg: "bg-orange-500/10 dark:bg-orange-500/20",
        iconColor: "text-orange-500",
        badge: "orange",
        count: "text-orange-600 dark:text-orange-400",
    },
    medium: {
        iconBg: "bg-amber-500/10 dark:bg-amber-500/20",
        iconColor: "text-amber-500",
        badge: "amber",
        count: "text-amber-600 dark:text-amber-400",
    },
};

const SEVERITY_LABELS: Record<Severity, Record<string, string>> = {
    critical: { en: "Critical", de: "Kritisch" },
    high: { en: "High", de: "Hoch" },
    medium: { en: "Medium", de: "Mittel" },
};

/**
 * A card that displays a security finding with severity icon, badge, and links to affected rulesets
 */
export default function InsecureSettingCard({ finding }: InsecureSettingCardProps) {
    const [t, { language }] = useTranslation("admin-dashboard");
    const styles = SEVERITY_STYLES[finding.severity];
    const severityLabel = SEVERITY_LABELS[finding.severity][language] ?? SEVERITY_LABELS[finding.severity].en;

    return (
        <div
            className={clsx(
                "rounded-xl border p-5",
                "border-zinc-950/10 bg-white dark:border-zinc-700 dark:bg-zinc-800/50",
            )}
        >
            <div className="flex items-start gap-4">
                <div className={clsx("shrink-0 rounded-lg p-2", styles.iconBg)}>
                    <ShieldExclamationIcon className={clsx("size-5", styles.iconColor)} />
                </div>

                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-zinc-950 dark:text-white">{finding.title}</span>
                        <Badge color={styles.badge}>{severityLabel}</Badge>
                    </div>
                    <p className="mt-1 text-sm/6 text-zinc-500 dark:text-zinc-400">{finding.description}</p>

                    {finding.affectedRulesets.length > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-1">
                            <span className="text-xs font-medium text-zinc-400 dark:text-zinc-500">
                                {t("label.affected")}
                            </span>
                            {finding.affectedRulesets.map((rs) => (
                                <Link
                                    key={rs.rulesetId}
                                    href={"/a/rulesets/$rulesetId/rules"}
                                    params={{ rulesetId: rs.rulesetId }}
                                    className="text-xs font-medium text-zinc-700 underline decoration-zinc-300 hover:text-zinc-950 hover:decoration-zinc-500 dark:text-zinc-300 dark:decoration-zinc-600 dark:hover:text-white dark:hover:decoration-zinc-400"
                                >
                                    {rs.rulesetName}
                                </Link>
                            ))}
                        </div>
                    )}
                </div>

                <span className={clsx("text-2xl font-bold tabular-nums", styles.count)}>{finding.count}</span>
            </div>
        </div>
    );
}
